import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  obtenerCarguesPendientesPacientesMasivo,
  obtenerDetalleCarguePacientesMasivo,
  obtenerHistoricoCarguePacientesMasivo,
  obtenerUltimoCargueEjecutadoPacientesMasivo,
  procesarCarguePacientesMasivo,
  programarCarguePacientesMasivo,
} from "../../api/carguePacientesMasivos/cargueMasivoApi";
import { fetchTowns } from "../../api/locationApi";
import {
  parseExcelBuffer,
  normalizeMunicipioCode,
  REQUIRED_HEADERS,
  REQUIRED_VALUES,
} from "./excelProcessing";

const DEFAULT_BATCH_SIZE = 1000;
const MAX_VISIBLE_ISSUES = 300;
const LAST_JOB_REFRESH_MS = 30000;
const HISTORY_LIMIT = 15;

const validateRows = (rows) =>
  rows.map((row, index) => {
    const missing = REQUIRED_VALUES.filter((field) => !String(row[field] ?? "").trim());
    return {
      row: index + 2,
      valid: missing.length === 0,
      missing,
    };
  });

const createEmptySummary = (total = 0) => ({
  total,
  procesados: 0,
  insertados_local: 0,
  actualizados_local: 0,
  reasignados_sede: 0,
  asignados_sede_principal: 0,
  conservados_sede_actual: 0,
  rechazados: 0,
  inactivados: 0,
});

const addSummary = (current, incoming, total) => ({
  total,
  procesados: (current?.procesados || 0) + (incoming?.procesados || 0),
  insertados_local: (current?.insertados_local || 0) + (incoming?.insertados_local || 0),
  actualizados_local: (current?.actualizados_local || 0) + (incoming?.actualizados_local || 0),
  reasignados_sede: (current?.reasignados_sede || 0) + (incoming?.reasignados_sede || 0),
  asignados_sede_principal:
    (current?.asignados_sede_principal || 0) + (incoming?.asignados_sede_principal || 0),
  conservados_sede_actual:
    (current?.conservados_sede_actual || 0) + (incoming?.conservados_sede_actual || 0),
  rechazados: (current?.rechazados || 0) + (incoming?.rechazados || 0),
  inactivados: (current?.inactivados || 0) + (incoming?.inactivados || 0),
});

const mapBatchItemsToGlobalRows = (items, rowOffset) =>
  (items || []).map((item) => ({
    ...item,
    row: typeof item?.row === "number" ? item.row + rowOffset : item?.row ?? null,
  }));

const appendVisibleItems = (current, incoming) => {
  if (!incoming?.length) {
    return current;
  }

  const merged = [...current, ...incoming];
  return merged.length > MAX_VISIBLE_ISSUES ? merged.slice(-MAX_VISIBLE_ISSUES) : merged;
};

const normalizeCodeSegment = (value) => String(value ?? "").trim().replace(/\.0+$/, "");

const buildDaneMunicipioCode = (departmentCode, municipioCode) => {
  const normalizedDepartment = normalizeCodeSegment(departmentCode);
  const normalizedMunicipio = normalizeCodeSegment(municipioCode);

  if (/^\d{1,2}$/.test(normalizedDepartment) && /^\d{1,3}$/.test(normalizedMunicipio)) {
    return `${normalizedDepartment.padStart(2, "0")}${normalizedMunicipio.padStart(3, "0")}`;
  }

  return "";
};

const getMunicipioCodeFromSource = (source) =>
  buildDaneMunicipioCode(
    source?.id_departamento ?? source?.idDepartamento ?? source?.departamento ?? source?.codDepartamento,
    source?.codigo ?? source?.Codigo ?? source?.codMuni ?? source?.cod_muni ?? source?.municipioCodigo
  ) ||
  normalizeMunicipioCode(
    source?.codigo ??
      source?.Codigo ??
      source?.codMuni ??
      source?.cod_muni ??
      source?.id_muni ??
      source?.municipioCodigo
  );

const getMunicipioNameFromSource = (source) =>
  String(
    source?.descripcion ??
      source?.Descripcion ??
      source?.nombre ??
      source?.Nombre ??
      source?.municipio ??
      source?.Municipio ??
      source?.ciudad ??
      source?.label ??
      ""
  ).trim();

const getMunicipioLabel = (municipio) => {
  const codigo = getMunicipioCodeFromSource(municipio);
  const nombre = getMunicipioNameFromSource(municipio);
  return nombre ? `${codigo} - ${nombre}` : codigo;
};

const getMunicipalityCountsFromRows = (rows, field = "Codmuni") => {
  const counts = {};

  rows.forEach((row) => {
    const code = normalizeMunicipioCode(row?.[field]);
    if (!code) {
      return;
    }
    counts[code] = (counts[code] || 0) + 1;
  });

  return counts;
};

const createEmptySelectionAlert = () => ({
  isOpen: false,
  action: "process",
  warnings: [],
  summary: {
    selectedLabel: "",
    selectedName: "",
    mismatchCount: 0,
    mismatchPercentage: 0,
    codmuniMismatchCount: 0,
    codmuniMismatchPercentage: 0,
    residenciaMismatchCount: 0,
    residenciaMismatchPercentage: 0,
    totalRows: 0,
    isBlocked: false,
    hasCodmuniMismatch: false,
    hasResidenciaThresholdExceeded: false,
  },
});

const createDefaultScheduledAt = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() + 5);
  now.setSeconds(0, 0);

  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const getMunicipioDisplayName = (label) => {
  const normalizedLabel = String(label ?? "").trim();
  if (normalizedLabel.includes(" - ")) {
    return normalizedLabel.split(" - ").slice(1).join(" - ").trim() || normalizedLabel;
  }
  return normalizedLabel;
};

const isNoLastJobMessage = (message) => {
  const normalizedMessage = String(message || "").trim().toLowerCase();
  return (
    normalizedMessage.includes("no hay cargues") ||
    normalizedMessage.includes("no hay errores registrados") ||
    normalizedMessage.includes("no hay errores") ||
    normalizedMessage.includes("ultimo cargue") ||
    normalizedMessage.includes("último cargue")
  );
};

const buildMunicipioWarnings = (rows, selectedMunicipio, availableMunicipios, municipioLabels = {}) => {
  const codmuniCounts = getMunicipalityCountsFromRows(rows, "Codmuni");
  const residenciaCounts = getMunicipalityCountsFromRows(rows, "Mpio_residencia");
  const excelCodmuni = Object.keys(codmuniCounts);
  const excelResidenciaMunicipios = Object.keys(residenciaCounts);
  const selectedCode = normalizeMunicipioCode(selectedMunicipio);

  const warnings = [];

  if (selectedCode === "") {
    return {
      warnings,
      summary: createEmptySelectionAlert().summary,
    };
  }

  const selectedMunicipioData = availableMunicipios.find(
    (municipio) => normalizeMunicipioCode(municipio?.codigo) === selectedCode
  );
  const selectedLabel = getMunicipioLabel(selectedMunicipioData || { codigo: selectedCode });
  const selectedName = getMunicipioDisplayName(selectedLabel);
  const totalRows = rows.length;
  let codmuniMismatchCount = 0;
  let residenciaMismatchCount = 0;

  excelCodmuni.forEach((municipio) => {
    const patientCount = codmuniCounts[municipio] || 0;

    if (municipio !== selectedCode) {
      codmuniMismatchCount += patientCount;
      warnings.push({
        type: "codmuni_diferente_al_seleccionado",
        municipio,
        municipioLabel: municipioLabels[municipio] || municipio,
        patientCount,
        percentage: totalRows > 0 ? (patientCount / totalRows) * 100 : 0,
        selectedLabel,
      });
    }
  });

  const blankCodmuniCount = (rows || []).reduce(
    (count, row) => count + (normalizeMunicipioCode(row?.Codmuni) === "" ? 1 : 0),
    0
  );

  if (blankCodmuniCount > 0) {
    codmuniMismatchCount += blankCodmuniCount;
    warnings.push({
      type: "codmuni_vacio_en_excel",
      municipio: "",
      patientCount: blankCodmuniCount,
      percentage: totalRows > 0 ? (blankCodmuniCount / totalRows) * 100 : 0,
      selectedLabel,
    });
  }

  excelResidenciaMunicipios.forEach((municipio) => {
    const patientCount = residenciaCounts[municipio] || 0;

    if (municipio !== selectedCode) {
      residenciaMismatchCount += patientCount;
      warnings.push({
        type: "mpio_residencia_diferente_al_seleccionado",
        municipio,
        municipioLabel: municipioLabels[municipio] || municipio,
        patientCount,
        percentage: totalRows > 0 ? (patientCount / totalRows) * 100 : 0,
        selectedLabel,
      });
    }
  });

  const blankResidenciaCount = (rows || []).reduce(
    (count, row) => count + (normalizeMunicipioCode(row?.Mpio_residencia) === "" ? 1 : 0),
    0
  );

  if (blankResidenciaCount > 0) {
    residenciaMismatchCount += blankResidenciaCount;
    warnings.push({
      type: "mpio_residencia_vacio_en_excel",
      municipio: "",
      patientCount: blankResidenciaCount,
      percentage: totalRows > 0 ? (blankResidenciaCount / totalRows) * 100 : 0,
      selectedLabel,
    });
  }

  const codmuniMismatchPercentage = totalRows > 0 ? (codmuniMismatchCount / totalRows) * 100 : 0;
  const residenciaMismatchPercentage =
    totalRows > 0 ? (residenciaMismatchCount / totalRows) * 100 : 0;
  const hasCodmuniMismatch = codmuniMismatchCount > 0;
  const hasResidenciaThresholdExceeded = residenciaMismatchPercentage > 5;

  return {
    warnings,
    summary: {
      selectedLabel,
      selectedName,
      mismatchCount: residenciaMismatchCount,
      mismatchPercentage: residenciaMismatchPercentage,
      codmuniMismatchCount,
      codmuniMismatchPercentage,
      residenciaMismatchCount,
      residenciaMismatchPercentage,
      totalRows,
      isBlocked: hasCodmuniMismatch || hasResidenciaThresholdExceeded,
      hasCodmuniMismatch,
      hasResidenciaThresholdExceeded,
    },
  };
};

const parseExcelFile = async (file) => {
  const buffer = await file.arrayBuffer();

  if (typeof Worker === "undefined") {
    return parseExcelBuffer(buffer);
  }

  try {
    return await new Promise((resolve, reject) => {
      const worker = new Worker(
        new URL("../../workers/carguePacientesMasivos.worker.js", import.meta.url)
      );

      worker.onmessage = (event) => {
        worker.terminate();

        if (event?.data?.type === "success") {
          resolve(event.data.payload);
          return;
        }

        reject(new Error(event?.data?.error || "No se pudo procesar el archivo Excel."));
      };

      worker.onerror = (event) => {
        worker.terminate();
        reject(new Error(event?.message || "No se pudo iniciar el procesamiento en segundo plano."));
      };

      worker.postMessage({ buffer }, [buffer]);
    });
  } catch (error) {
    console.warn("[useCarguePacientesMasivos] Fallback a procesamiento local del Excel:", error);
    return parseExcelBuffer(buffer);
  }
};

const resolveMunicipioLabels = async (rows, availableMunicipios) => {
  const knownLabels = Object.fromEntries(
    (availableMunicipios || []).map((municipio) => [
      getMunicipioCodeFromSource(municipio),
      municipio.label,
    ])
  );
  const excelMunicipios = [
    ...new Set([
      ...Object.keys(getMunicipalityCountsFromRows(rows, "Codmuni")),
      ...Object.keys(getMunicipalityCountsFromRows(rows, "Mpio_residencia")),
    ]),
  ];
  const unknownCodes = excelMunicipios.filter((codigo) => codigo && !knownLabels[codigo]);

  if (unknownCodes.length === 0) {
    return knownLabels;
  }

  const departmentCodes = [...new Set(unknownCodes.map((codigo) => codigo.slice(0, 2)).filter(Boolean))];

  await Promise.all(
    departmentCodes.map(async (departmentCode) => {
      const towns = await fetchTowns(departmentCode);
      (towns || []).forEach((town) => {
        const codigo = getMunicipioCodeFromSource(town);
        const nombre = getMunicipioNameFromSource(town);
        if (codigo && nombre && !knownLabels[codigo]) {
          knownLabels[codigo] = `${codigo} - ${nombre}`;
        }
      });
    })
  );

  return knownLabels;
};

export function useCarguePacientesMasivos(availableSedes = []) {
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]);
  const [selectedMunicipio, setSelectedMunicipio] = useState("");
  const [executionMode, setExecutionMode] = useState("process");
  const [scheduledAt, setScheduledAt] = useState(createDefaultScheduledAt);
  const [missingHeaders, setMissingHeaders] = useState([]);
  const [loadingFile, setLoadingFile] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [issueTotals, setIssueTotals] = useState({
    errors: 0,
    warnings: 0,
  });
  const [progress, setProgress] = useState({
    currentBatch: 0,
    totalBatches: 0,
    processedRows: 0,
    percentage: 0,
    batchSize: DEFAULT_BATCH_SIZE,
  });
  const [timing, setTiming] = useState({
    totalElapsedMs: 0,
    lastBatchMs: 0,
    averageBatchMs: 0,
    backendTotalMs: 0,
    backendLastBatchMs: 0,
  });
  const [selectionAlert, setSelectionAlert] = useState({
    ...createEmptySelectionAlert(),
  });
  const [availableMunicipioLabels, setAvailableMunicipioLabels] = useState({});
  const [currentJobId, setCurrentJobId] = useState(0);
  const [pendingJobs, setPendingJobs] = useState([]);
  const [loadingPendingJobs, setLoadingPendingJobs] = useState(false);
  const [lastJob, setLastJob] = useState(null);
  const [loadingLastJob, setLoadingLastJob] = useState(false);
  const [historyJobs, setHistoryJobs] = useState([]);
  const [loadingHistoryJobs, setLoadingHistoryJobs] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [jobIssuesById, setJobIssuesById] = useState({});
  const [loadingJobIssuesId, setLoadingJobIssuesId] = useState(null);

  const loadDashboardJobs = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoadingPendingJobs(true);
      setLoadingLastJob(true);
    }

    try {
      const pendingResponse = await obtenerCarguesPendientesPacientesMasivo();
      const normalizedPendingJobs = Array.isArray(pendingResponse?.data) ? pendingResponse.data : [];
      setPendingJobs(normalizedPendingJobs);

      if (normalizedPendingJobs.length > 0) {
        setLastJob(null);
        return;
      }

      const lastExecutedResponse = await obtenerUltimoCargueEjecutadoPacientesMasivo();
      setLastJob(lastExecutedResponse?.data || null);
    } catch (error) {
      console.error("[useCarguePacientesMasivos] Error consultando dashboard de jobs:", error);
      const backendMessage = error?.response?.data?.message || "";
      const statusCode = Number(error?.response?.status || 0);
      const shouldIgnoreMissingJob = statusCode === 404 || isNoLastJobMessage(backendMessage);

      if (!silent && !shouldIgnoreMissingJob) {
        toast.error("No se pudo consultar el estado de los cargues del usuario.");
      }

      setPendingJobs([]);
      setLastJob(null);
    } finally {
      if (!silent) {
        setLoadingPendingJobs(false);
        setLoadingLastJob(false);
      }
    }
  }, []);

  const loadHistoryJobs = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoadingHistoryJobs(true);
    }

    try {
      const response = await obtenerHistoricoCarguePacientesMasivo();
      setHistoryJobs(Array.isArray(response?.data) ? response.data.slice(0, HISTORY_LIMIT) : []);
    } catch (error) {
      console.error("[useCarguePacientesMasivos] Error consultando historico de jobs:", error);
      if (!silent) {
        toast.error("No se pudo consultar el historico de cargues.");
      }
      setHistoryJobs([]);
    } finally {
      if (!silent) {
        setLoadingHistoryJobs(false);
      }
    }
  }, []);

  const loadJobIssues = useCallback(
    async (jobId) => {
      const normalizedJobId = Number(jobId || 0);
      if (normalizedJobId <= 0) {
        return [];
      }

      setLoadingJobIssuesId(normalizedJobId);

      try {
        const response = await obtenerDetalleCarguePacientesMasivo(normalizedJobId);
        const normalizedIssues = (response?.data || []).map((item) => ({
          row: item?.row_number ?? item?.row ?? null,
          identificacion: item?.identificacion || "-",
          status: String(item?.status || "").toLowerCase(),
          message: item?.message || "",
        }));

        setJobIssuesById((current) => ({
          ...current,
          [normalizedJobId]: normalizedIssues,
        }));

        return normalizedIssues;
      } catch (error) {
        console.error("[useCarguePacientesMasivos] Error consultando detalle del job:", error);
        toast.error("No se pudo consultar el detalle del cargue seleccionado.");
        setJobIssuesById((current) => ({
          ...current,
          [normalizedJobId]: [],
        }));
        return [];
      } finally {
        setLoadingJobIssuesId(null);
      }
    },
    []
  );

  const loadLastJob = useCallback(
    async ({ silent = false } = {}) => {
      await loadDashboardJobs({ silent });
    },
    [loadDashboardJobs]
  );

  const loadLastJobErrors = useCallback(async () => {
    if (!lastJob?.id) {
      return [];
    }

    if (jobIssuesById[lastJob.id]) {
      return jobIssuesById[lastJob.id];
    }

    return loadJobIssues(lastJob.id);
  }, [jobIssuesById, lastJob, loadJobIssues]);

  useEffect(() => {
    loadDashboardJobs();
  }, [loadDashboardJobs]);

  useEffect(() => {
    const hasActiveJobs = pendingJobs.some((job) =>
      ["scheduled", "running"].includes(String(job?.status || "").toLowerCase())
    );

    if (!hasActiveJobs) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      loadDashboardJobs({ silent: true });
    }, LAST_JOB_REFRESH_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [pendingJobs, loadDashboardJobs]);

  const availableMunicipioCodes = useMemo(() => {
    const grouped = new Set();

    (availableSedes || []).forEach((sede) => {
      const codigo = normalizeMunicipioCode(sede?.id_muni);
      if (codigo !== "") {
        grouped.add(codigo);
      }
    });

    return Array.from(grouped).sort((a, b) => a.localeCompare(b));
  }, [availableSedes]);

  useEffect(() => {
    let cancelled = false;

    if (availableMunicipioCodes.length === 0) {
      setAvailableMunicipioLabels({});
      return () => {
        cancelled = true;
      };
    }

    const loadMunicipioLabels = async () => {
      const labelsByCode = {};
      const availableCodeSet = new Set(availableMunicipioCodes);
      const departmentCodes = [
        ...new Set(availableMunicipioCodes.map((codigo) => codigo.slice(0, 2)).filter(Boolean)),
      ];

      await Promise.all(
        departmentCodes.map(async (departmentCode) => {
          const towns = await fetchTowns(departmentCode);
          (towns || []).forEach((town) => {
            const codigo = getMunicipioCodeFromSource(town);
            const nombre = getMunicipioNameFromSource(town);
            if (codigo && nombre && availableCodeSet.has(codigo)) {
              labelsByCode[codigo] = `${codigo} - ${nombre}`;
            }
          });
        })
      );

      if (!cancelled) {
        setAvailableMunicipioLabels(labelsByCode);
      }
    };

    loadMunicipioLabels();

    return () => {
      cancelled = true;
    };
  }, [availableMunicipioCodes]);

  const availableMunicipios = useMemo(
    () =>
      availableMunicipioCodes
        .map((codigo) => {
          const label = availableMunicipioLabels[codigo] || codigo;
          return {
            codigo,
            nombre: label.includes(" - ") ? getMunicipioDisplayName(label) : "",
            label,
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label)),
    [availableMunicipioCodes, availableMunicipioLabels]
  );

  const validation = useMemo(() => validateRows(rows), [rows]);
  const validRowsCount = useMemo(
    () => validation.reduce((count, item) => count + (item.valid ? 1 : 0), 0),
    [validation]
  );
  const excelMunicipios = useMemo(
    () =>
      Object.entries(getMunicipalityCountsFromRows(rows, "Codmuni")).map(([codigo, count]) => ({
        codigo,
        count,
      })),
    [rows]
  );

  const handleFileSelected = async (file) => {
    if (!file) {
      return;
    }

    const extension = file.name.split(".").pop()?.toLowerCase();
    if (!["xlsx", "xls"].includes(extension || "")) {
      toast.error("Solo se permiten archivos Excel .xlsx o .xls");
      return;
    }

    setLoadingFile(true);
    try {
      const { missingHeaders: missing, normalizedRows, identifierErrors } = await parseExcelFile(file);
      setMissingHeaders(missing);

      if (missing.length > 0) {
        setRows([]);
        setFileName(file.name);
        setSummary(null);
        setErrors([]);
        setWarnings([]);
        setIssueTotals({
          errors: 0,
          warnings: 0,
        });
        setProgress({
          currentBatch: 0,
          totalBatches: 0,
          processedRows: 0,
          percentage: 0,
          batchSize: DEFAULT_BATCH_SIZE,
        });
        setTiming({
          totalElapsedMs: 0,
          lastBatchMs: 0,
          averageBatchMs: 0,
          backendTotalMs: 0,
          backendLastBatchMs: 0,
        });
        toast.error("El archivo no contiene todas las columnas requeridas.");
        return;
      }

      if (identifierErrors.length > 0) {
        setRows([]);
        setFileName(file.name);
        setSummary(null);
        setErrors(identifierErrors.slice(0, MAX_VISIBLE_ISSUES));
        setWarnings([]);
        setIssueTotals({
          errors: identifierErrors.length,
          warnings: 0,
        });
        setProgress({
          currentBatch: 0,
          totalBatches: 0,
          processedRows: 0,
          percentage: 0,
          batchSize: DEFAULT_BATCH_SIZE,
        });
        setTiming({
          totalElapsedMs: 0,
          lastBatchMs: 0,
          averageBatchMs: 0,
          backendTotalMs: 0,
          backendLastBatchMs: 0,
        });
        toast.error(`El archivo tiene ${identifierErrors.length} error(es) en identificación o Unic_ID. Corrige y vuelve a cargar.`);
        return;
      }

      setRows(normalizedRows);
      setFileName(file.name);
      setSummary(null);
      setErrors([]);
      setWarnings([]);
      setIssueTotals({
        errors: 0,
        warnings: 0,
      });
      setProgress({
        currentBatch: 0,
        totalBatches: 0,
        processedRows: 0,
        percentage: 0,
        batchSize: DEFAULT_BATCH_SIZE,
      });
      setTiming({
        totalElapsedMs: 0,
        lastBatchMs: 0,
        averageBatchMs: 0,
        backendTotalMs: 0,
        backendLastBatchMs: 0,
      });

      toast.success(`Archivo cargado con ${normalizedRows.length} filas.`);
    } catch (error) {
      console.error("[useCarguePacientesMasivos] Error leyendo Excel:", error);
      toast.error("No se pudo leer el archivo Excel.");
    } finally {
      setLoadingFile(false);
    }
  };

  const executeUpload = async () => {
    let activeJobId = 0;
    setProcessing(true);
    setCurrentJobId(0);
    try {
      const totalRows = rows.length;
      const processStartedAt = Date.now();

      setSummary(createEmptySummary(totalRows));
      setErrors([]);
      setWarnings([]);
      setIssueTotals({ errors: 0, warnings: 0 });
      setProgress({
        currentBatch: 1,
        totalBatches: 1,
        processedRows: 0,
        percentage: 50,
        batchSize: totalRows,
      });
      setTiming({
        totalElapsedMs: 0,
        lastBatchMs: 0,
        averageBatchMs: 0,
        backendTotalMs: 0,
        backendLastBatchMs: 0,
      });

      const result = await procesarCarguePacientesMasivo({
        rows,
        selectedMunicipio,
      });

      activeJobId = Number(result?.data?.job_id || 0);
      if (activeJobId > 0) {
        setCurrentJobId(activeJobId);
      }

      const jobSummary = result.data?.summary || createEmptySummary(totalRows);
      const jobErrors = result.data?.errors || [];
      const jobWarnings = result.data?.warnings || [];

      setSummary(jobSummary);
      setErrors(jobErrors.slice(0, MAX_VISIBLE_ISSUES));
      setWarnings(jobWarnings.slice(0, MAX_VISIBLE_ISSUES));
      setIssueTotals({
        errors: jobErrors.length,
        warnings: jobWarnings.length,
      });

      const totalElapsedMs = Date.now() - processStartedAt;
      const backendElapsedMs = Number(result.data?.metrics?.backend_ms || 0);

      setProgress({
        currentBatch: 1,
        totalBatches: 1,
        processedRows: totalRows,
        percentage: 100,
        batchSize: totalRows,
      });
      setTiming({
        totalElapsedMs,
        lastBatchMs: totalElapsedMs,
        averageBatchMs: totalElapsedMs,
        backendTotalMs: backendElapsedMs,
        backendLastBatchMs: backendElapsedMs,
      });

      toast.success("Cargue masivo procesado exitosamente vía Stored Procedure.");
      await loadDashboardJobs({ silent: true });
      if (historyModalOpen) {
        await loadHistoryJobs({ silent: true });
      }
    } catch (error) {
      console.error("[useCarguePacientesMasivos] Error procesando cargue:", error);
      toast.error(
        error?.response?.data?.message ||
          "No se pudo procesar el cargue masivo. Revisa el archivo e intenta de nuevo."
      );
      await loadDashboardJobs({ silent: true });
      if (historyModalOpen) {
        await loadHistoryJobs({ silent: true });
      }
    } finally {
      setCurrentJobId(0);
      setProcessing(false);
    }
  };

  const validateBeforeSubmit = async (action) => {
    if (rows.length === 0) {
      toast.error("Debes cargar un archivo con filas válidas antes de continuar.");
      return false;
    }

    if (missingHeaders.length > 0) {
      toast.error("El archivo aun tiene columnas faltantes.");
      return false;
    }

    if (selectedMunicipio === "") {
      toast.warning("Debes seleccionar un municipio antes de continuar.");
      return false;
    }

    const municipioLabels = await resolveMunicipioLabels(rows, availableMunicipios);
    const selectionAlertData = buildMunicipioWarnings(
      rows,
      selectedMunicipio,
      availableMunicipios,
      municipioLabels
    );

    if (selectionAlertData.warnings.length > 0) {
      setSelectionAlert({
        isOpen: true,
        action,
        warnings: selectionAlertData.warnings,
        summary: selectionAlertData.summary,
      });
      return false;
    }

    return true;
  };

  const executeScheduleUpload = async () => {
    setProcessing(true);
    try {
      setSummary(null);
      setErrors([]);
      setWarnings([]);
      setIssueTotals({
        errors: 0,
        warnings: 0,
      });
      setProgress({
        currentBatch: 0,
        totalBatches: 0,
        processedRows: 0,
        percentage: 0,
        batchSize: DEFAULT_BATCH_SIZE,
      });
      setTiming({
        totalElapsedMs: 0,
        lastBatchMs: 0,
        averageBatchMs: 0,
        backendTotalMs: 0,
        backendLastBatchMs: 0,
      });

      const response = await programarCarguePacientesMasivo({
        rows,
        selectedMunicipio,
        scheduledAt,
      });

      await loadDashboardJobs({ silent: true });
      if (historyModalOpen) {
        await loadHistoryJobs({ silent: true });
      }

      toast.success(
        response?.message || "Cargue programado correctamente. El backend lo procesara en la fecha indicada."
      );
    } catch (error) {
      console.error("[useCarguePacientesMasivos] Error programando cargue:", error);
      toast.error(
        error?.response?.data?.message || "No se pudo programar el cargue con la fecha indicada."
      );
    } finally {
      setProcessing(false);
    }
  };

  const processUpload = async () => {
    const canContinue = await validateBeforeSubmit("process");
    if (!canContinue) {
      return;
    }

    await executeUpload();
  };

  const scheduleUpload = async () => {
    if (String(scheduledAt || "").trim() === "") {
      toast.warning("Debes indicar la fecha y hora en la que se ejecutara el cargue.");
      return;
    }

    const canContinue = await validateBeforeSubmit("schedule");
    if (!canContinue) {
      return;
    }

    await executeScheduleUpload();
  };

  const submitUpload = async () => {
    if (executionMode === "schedule") {
      await scheduleUpload();
      return;
    }

    await processUpload();
  };

  const confirmSelectionAlert = async () => {
    if (selectionAlert.summary?.hasCodmuniMismatch) {
      toast.error("El Codmuni del Excel debe coincidir con el municipio seleccionado. Revise la base y vuelva a intentarlo.");
      return;
    }

    if (selectionAlert.summary?.hasResidenciaThresholdExceeded) {
      toast.error("Con mas del 5% de Mpio_residencia diferentes al municipio seleccionado no se puede cargar la base de datos. Revise la base y vuelva a intentarlo.");
      return;
    }

    const action = selectionAlert.action;
    setSelectionAlert({
      ...createEmptySelectionAlert(),
    });

    if (action === "schedule") {
      await executeScheduleUpload();
      return;
    }

    await executeUpload();
  };

  const cancelSelectionAlert = () => {
    setSelectionAlert({
      ...createEmptySelectionAlert(),
    });
  };

  const resetState = () => {
    setFileName("");
    setRows([]);
    setSelectedMunicipio("");
    setExecutionMode("process");
    setScheduledAt(createDefaultScheduledAt());
    setMissingHeaders([]);
    setSummary(null);
    setErrors([]);
    setWarnings([]);
    setCurrentJobId(0);
    setIssueTotals({
      errors: 0,
      warnings: 0,
    });
    setProgress({
      currentBatch: 0,
      totalBatches: 0,
      processedRows: 0,
      percentage: 0,
      batchSize: DEFAULT_BATCH_SIZE,
    });
    setTiming({
      totalElapsedMs: 0,
      lastBatchMs: 0,
      averageBatchMs: 0,
      backendTotalMs: 0,
      backendLastBatchMs: 0,
    });
    setSelectionAlert({
      ...createEmptySelectionAlert(),
    });
  };

  return {
    state: {
      fileName,
      rows,
      missingHeaders,
      loadingFile,
      processing,
      summary,
      errors,
      warnings,
      issueTotals,
      progress,
      timing,
      selectedMunicipio,
      executionMode,
      scheduledAt,
      availableMunicipios,
      excelMunicipios,
      selectionAlert,
      currentJobId,
      pendingJobs,
      loadingPendingJobs,
      lastJob,
      loadingLastJob,
      historyJobs,
      loadingHistoryJobs,
      historyModalOpen,
      jobIssuesById,
      loadingJobIssuesId,
      validation,
      validRowsCount,
      requiredHeaders: REQUIRED_HEADERS,
    },
    actions: {
      handleFileSelected,
      processUpload,
      scheduleUpload,
      submitUpload,
      resetState,
      setSelectedMunicipio,
      setExecutionMode,
      setScheduledAt,
      confirmSelectionAlert,
      cancelSelectionAlert,
      loadLastJob,
      loadLastJobErrors,
      loadDashboardJobs,
      loadHistoryJobs,
      loadJobIssues,
      setHistoryModalOpen,
    },
  };
}
